import {
  Component,
  inject,
  ViewContainerRef,
  ComponentRef,
  viewChild,
  signal,
  input,
  output,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';

import { ModalData, ModalService } from '../../../../core/services/modal.service';
import { ModalComponent } from '../modal/modal.component';
import { ButtonComponent } from '../../button/button.component';

@Component({
  selector: 'app-modal-host-entry',
  standalone: true,
  imports: [ModalComponent, ButtonComponent],
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
  template: `
    <app-modal
      [title]="modal().config.title"
      [subtitle]="modal().config.subtitle || ''"
      [icon]="modal().config.icon || ''"
      [size]="modal().config.size || 'md'"
      [isOpen]="true"
      [managedByHost]="true"
      [zIndex]="70 + index() * 10"
      (closed)="handleClose()"
    >
      <div slot="content">
        <ng-container #dynamicContent></ng-container>
      </div>

      @if (!hasCustomActions()) {
      <div slot="actions">
        <app-button variant="outline" (click)="handleCancel()">
          Cancel
        </app-button>
        <app-button variant="primary" (click)="handleSuccess()">
          Save
        </app-button>
      </div>
      }
    </app-modal>
  `,
})
export class ModalHostEntryComponent implements AfterViewInit, OnDestroy {
  modal = input.required<ModalData>();
  index = input<number>(0);
  closed = output<void>();

  private dynamicContent = viewChild('dynamicContent', { read: ViewContainerRef });
  private dynamicComponentRef: ComponentRef<any> | null = null;
  hasCustomActions = signal(false);

  ngAfterViewInit() {
    const container = this.dynamicContent();
    const modalData = this.modal();

    if (container && modalData) {
      this.dynamicComponentRef = container.createComponent(modalData.component);

      // Pass data to the component instance, skipping getter-only properties
      if (modalData.data) {
        const instance = this.dynamicComponentRef.instance;
        for (const [key, value] of Object.entries(modalData.data)) {
          const descriptor = Object.getOwnPropertyDescriptor(
            Object.getPrototypeOf(instance),
            key
          );
          if (descriptor && descriptor.get && !descriptor.set) continue;
          try {
            if (instance[key] && typeof instance[key].set === 'function') {
              instance[key].set(value);
            } else {
              instance[key] = value;
            }
          } catch {
            // Property may be read-only (signal input, etc.) — skip silently
          }
        }
      }

      // Check if the component has custom actions
      this.hasCustomActions.set(
        this.dynamicComponentRef.instance.hasCustomActions || false
      );

      // Listen to component events if they exist
      if (this.dynamicComponentRef.instance.formSubmit) {
        this.dynamicComponentRef.instance.formSubmit.subscribe(() => {
          this.handleSuccess();
        });
      }

      if (this.dynamicComponentRef.instance.formCancel) {
        this.dynamicComponentRef.instance.formCancel.subscribe(() => {
          this.handleCancel();
        });
      }

      // Manually trigger change detection for the dynamically created component (required for Zoneless)
      this.dynamicComponentRef.changeDetectorRef.detectChanges();
    }
  }

  handleClose() {
    this.closed.emit();
  }

  handleCancel() {
    this.modal().onCancel?.();
    this.closed.emit();
  }

  handleSuccess() {
    this.modal().onSuccess?.();
    this.closed.emit();
  }

  ngOnDestroy() {
    if (this.dynamicComponentRef) {
      this.dynamicComponentRef.destroy();
      this.dynamicComponentRef = null;
    }
  }
}

@Component({
  selector: 'app-modal-host',
  standalone: true,
  imports: [ModalHostEntryComponent],
  template: `
    @for (modal of modalService.stack(); track modal.id; let idx = $index) {
      <app-modal-host-entry
        animate.leave="hv-modal-host-leave"
        [modal]="modal"
        [index]="idx"
        (closed)="closeModal(modal)"
      />
    }
  `,
})
export class ModalHostComponent {
  protected modalService = inject(ModalService);

  closeModal(modal: ModalData) {
    this.modalService.closeById(modal.id);
  }
}
